import React from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import { composeQA } from "@multiweb/reactUtils";
import { IMenuItemData } from "@multiweb/types/contracts";
import { IAppState, QAProps } from "@multiweb/types/react";
import { createMenuItemActivatedAction } from "../../actions";
import { isActionItemFocused, isItemFocused, MenuFocusableItemType, NavigationSection } from "../../navigation";
import {
    areProfileWindowActionsEnabled,
    getFormItemByProfileId,
    getProfileFormCode,
    getProfileTitle,
} from "../../selectors";
import { MenuItemActivatedType } from "../../types";
import { createItemIdForProfile, createMenuItemKeyActivationHandler, createOpenableInTabItemMouseActivationHandler } from "../../utils";
import { MenuItemComponent, MenuItemComponentProps } from "../MenuItemComponent";
import { ProfileRightPartComponent } from "../ProfileRightPartComponent";

export type ProfileItemProps = {
    /** Identifikátor profilu. */
    profileId: number;
    /** Sekce, ve které je profil vykreslený. Slouží pro určování zaměření. */
    section: NavigationSection;
    /** Id formuláře pod kterým je profil vykreslený, pokud není profil vykreslený samostatně. */
    parentFormItemId?: number;
    /** Příznak umožňující povolit/zakázat zobrazení kontextové akce pro otevření. Výchozí hodnota je true. */
    openActionEnabled?: boolean;
} & QAProps;

type StateProps = {
    formItem?: IMenuItemData;
    formCode?: string;
    label: string;
    windowActionsEnabled: boolean;
    isFocused: boolean;
    isActionItemFocused: boolean;
};

type DispatchProps = {
    menuItemActivated: typeof createMenuItemActivatedAction;
};

const mapStateToProps: MapStateToProps<StateProps, ProfileItemProps, IAppState> = (state, { profileId, parentFormItemId, section }) => {
    const itemId = createItemIdForProfile(profileId, parentFormItemId);
    return {
        formItem: getFormItemByProfileId(state, profileId),
        formCode: getProfileFormCode(state, profileId),
        label: getProfileTitle(state, profileId),
        windowActionsEnabled: areProfileWindowActionsEnabled(state, profileId),
        isFocused: isItemFocused(state, { itemId, type: MenuFocusableItemType.profile, section }),
        isActionItemFocused: isActionItemFocused(state, { parentItemId: itemId, section }),
    };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, ProfileItemProps> = ({
    menuItemActivated: createMenuItemActivatedAction,
});

const mergeProps: MergeProps<StateProps, DispatchProps, ProfileItemProps, MenuItemComponentProps> = (
    { formItem, formCode, label, windowActionsEnabled, isFocused, isActionItemFocused },
    { menuItemActivated },
    { profileId, parentFormItemId, openActionEnabled = true, qa, section },
) => {
    const menuItemActivatedType = MenuItemActivatedType.profile;
    const itemId = createItemIdForProfile(profileId, parentFormItemId);
    return {
        iconCode: formItem ? formItem.icon : undefined,
        label,
        onClick: createOpenableInTabItemMouseActivationHandler(itemId, menuItemActivatedType, section, menuItemActivated),
        onKeyUp: createMenuItemKeyActivationHandler(itemId, menuItemActivatedType, section, menuItemActivated),
        rightPartComponentCreator: (isActive) => <ProfileRightPartComponent {...{ isActive, isActionItemFocused, windowActionsEnabled, openActionEnabled, section, parentFormItemId, profileId }} />,
        isFocused,
        qa: composeQA(composeQA(profileId, formCode), qa),
    };
};

/**
 * Získává údaje pro zobrazení položky profilu formuláře. Využívá ikonu rodičovského formuláře.
 */
export const ProfileItem = connect(mapStateToProps, mapDispatchToProps, mergeProps)(MenuItemComponent);
